import type { AuthSessionRequest, AuthSessionResponse, SessionData, MergeUserRequest, MergeUserResponse, MergeUserData } from "../types";
import { CookieManager } from "./cookie.utils";

/**
 * API Service for Sophi authentication and session management
 */
export class ApiService {
  private baseUrl: string;
  private apiKey: string;
  private clientId: string | null = null;
  private userId: string | undefined = undefined;
  private storedUserId: string | null = null; // Track the userId from cookies for merge detection
  private cookieManager: CookieManager;
  /**
   * Whether the SDK is allowed to read/write cookies. When false the SDK runs
   * in anonymous mode: the session lives only in memory and no cookies are
   * touched, so the user starts fresh on every page load.
   */
  private storageConsent: boolean;
  /**
   * In-memory access token. Always retained (even in anonymous mode where no
   * cookie exists) so a merge can run when storage consent is granted later.
   */
  private accessToken: string | null = null;
  /**
   * Host-provided userId held back while anonymous. Used to merge the guest
   * session into the logged-in user once storage consent is granted.
   */
  private pendingUserId: string | null = null;

  constructor(baseUrl: string, apiKey: string, userId?: string, storageConsent: boolean = true) {
    this.baseUrl = baseUrl;
    this.apiKey = apiKey;
    this.storageConsent = storageConsent;
    this.cookieManager = new CookieManager(CookieManager.buildNamespace(baseUrl, apiKey));

    if (storageConsent) {
      this.userId = userId;
      this.restoreSessionFromCookies();
    } else {
      // Anonymous mode: never read cookies, run as a guest, but remember the
      // userId so we can merge the guest session if consent is granted later.
      this.pendingUserId = userId || null;
      this.userId = undefined;
    }
  }

  /**
   * Create a client session by calling the authentication endpoint
   * @param request - Session request with optional externalUserId and metadata
   * @returns Session data including accessToken, sessionId, etc.
   */
  async createSession(request: AuthSessionRequest): Promise<SessionData> {
    const url = `${this.baseUrl}/auth/v1/client/session`;

    try {
      const headers: Record<string, string> = {
        "Content-Type": "application/json",
        Accept: "application/json",
        "x-api-key": this.apiKey,
      };
      if (this.clientId) {
        headers["x-sophi-client-id"] = this.clientId;
        // Check if we need to merge: user changed from stored (could be empty) to current
        const normalizedStored = this.storedUserId || null;
        const normalizedCurrent = this.userId || null;
        if (normalizedStored !== normalizedCurrent && normalizedCurrent) {
          try {
            await this.mergeUser({ userId: normalizedCurrent });
            this.storedUserId = normalizedCurrent;
          } catch (_mergeError) {
            // Merge failed (e.g. server reset, stale clientId no longer exists)
            // Clear stale session data and fall through to create a fresh session
            this.clearStoredClientInfo();
            delete headers["x-sophi-client-id"];
            this.storedUserId = normalizedCurrent;
          }
        }
      }

      const response = await fetch(url, {
        method: "POST",
        headers,
        body: JSON.stringify({
          externalUserId: this.userId,
          metadata: request.metadata,
        }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to create session: ${response.status} ${response.statusText}. ${errorText}`);
      }

      const data: AuthSessionResponse = await response.json();

      if (!data.success) {
        throw new Error(`Session creation failed: ${data.message || "Unknown error"}`);
      }

      if (!data.data) {
        throw new Error("Session data is missing from response");
      }
      this.persistClientInfo(data.data.clientId, data.data.accessToken, this.userId);
      return data.data;
    } catch (error) {
      if (error instanceof Error) {
        throw error;
      }
      throw new Error(`Unexpected error during session creation: ${String(error)}`);
    }
  }

  /**
   * Validate session data
   */
  validateSessionData(sessionData: SessionData): boolean {
    return !!(sessionData.accessToken && sessionData.sessionId && sessionData.clientId && sessionData.companyCode && sessionData.expiresAt);
  }

  /**
   * Merge guest user with logged-in user
   * @param request - Merge request with guestId and userId
   * @returns Merge result data including the resulting clientId
   */
  async mergeUser(request: MergeUserRequest): Promise<MergeUserData> {
    const url = `${this.baseUrl}/auth/v1/client/update`;

    try {
      if (!this.clientId) {
        throw new Error("Client ID is not set. Please create a session first.");
      }
      // Prefer the in-memory token so merge works in anonymous mode (no cookie).
      const accessToken = this.accessToken ?? this.cookieManager.get("accessToken");
      if (!accessToken) {
        throw new Error("Access token is not available. Please create a session first.");
      }
      const response = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          Authorization: `Bearer ${accessToken}`,
          "x-sophi-client-id": this.clientId,
        },
        body: JSON.stringify({
          externalUserId: request.userId,
        }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to merge user: ${response.status} ${response.statusText}. ${errorText}`);
      }

      const data: MergeUserResponse = await response.json();

      if (!data.success) {
        throw new Error(`User merge failed: ${data.message || "Unknown error"}`);
      }

      if (!data.data) {
        throw new Error("Merge data is missing from response");
      }

      return data.data;
    } catch (error) {
      if (error instanceof Error) {
        throw error;
      }
      throw new Error(`Unexpected error during user merge: ${String(error)}`);
    }
  }

  /**
   * Grant storage consent at runtime (e.g. the user accepted cookies while the
   * chat was open).
   *
   * - Guest: persists the existing in-memory anonymous session to cookies so it
   *   simply becomes persistent (same session continues).
   * - Logged-in user: merges the anonymous guest session into the user AND opens
   *   a fresh session for that user (via {@link createSession}) so we receive the
   *   user's auth data (accessToken, sessionId, clientId, layout, ...) from the
   *   API — the same merge-then-new-session flow used on a normal login.
   *
   * @param request - Optional metadata for the new session (logged-in case).
   * @returns the newly created {@link SessionData} when a logged-in session was
   *          opened, or null when nothing new was created (guest, or no active
   *          in-memory session yet).
   */
  async enableStorageConsent(request: AuthSessionRequest = {}): Promise<SessionData | null> {
    this.storageConsent = true;

    // No in-memory session yet (e.g. consent granted before the anonymous
    // session was created). Just promote the pending identity so the next
    // createSession runs for the user.
    if (!this.clientId || !this.accessToken) {
      if (this.pendingUserId && !this.userId) {
        this.userId = this.pendingUserId;
      }
      this.pendingUserId = null;
      return null;
    }

    // Persist the existing in-memory session so the merge + new-session requests
    // can authenticate with the current clientId + token.
    if (CookieManager.canUseCookies()) {
      this.cookieManager.set("clientId", this.clientId);
      this.cookieManager.set("accessToken", this.accessToken);
      this.cookieManager.set("externalUserId", this.userId || "");
    }

    // Guest: the anonymous session simply becomes persistent — nothing else to do.
    if (!this.pendingUserId) {
      return null;
    }

    // Logged-in user: promote the identity, then create a session. createSession
    // detects the guest -> user transition (storedUserId is still null/empty),
    // runs mergeUser, opens a fresh session for the user, and persists the new
    // credentials to cookies.
    this.userId = this.pendingUserId;
    this.pendingUserId = null;

    return this.createSession(request);
  }

  /**
   * Withdraw storage consent at runtime. Clears all cookies but keeps the
   * in-memory session so the current chat keeps working until the page is left.
   */
  disableStorageConsent(): void {
    this.storageConsent = false;
    if (CookieManager.canUseCookies()) {
      this.cookieManager.clearAll();
    }
  }

  private persistClientInfo(clientId: string, accessToken: string, userId?: string): void {
    this.clientId = clientId;
    this.accessToken = accessToken;
    this.userId = userId;
    this.storedUserId = userId || null;

    // Anonymous mode: keep everything in memory only, never touch cookies.
    if (!this.storageConsent || !CookieManager.canUseCookies()) {
      return;
    }

    this.cookieManager.set("clientId", clientId);
    this.cookieManager.set("accessToken", accessToken);
    // Always save externalUserId (even if empty) to track the session state
    this.cookieManager.set("externalUserId", userId || "");
  }

  private restoreSessionFromCookies(): void {
    if (!this.storageConsent || !CookieManager.canUseCookies()) {
      return;
    }

    const storedUserId = this.cookieManager.get("externalUserId");
    const storedClientId = this.cookieManager.get("clientId");

    // Store the userId from cookies for merge detection later
    this.storedUserId = storedUserId || null;

    // If no clientId in cookies, nothing to restore
    if (!storedClientId) {
      return;
    }

    // Normalize empty values (undefined, null, empty string) to null for comparison
    const normalizedStoredUserId = storedUserId || null;
    const normalizedCurrentUserId = this.userId || null;

    // If userIds match, restore both clientId and userId
    if (normalizedStoredUserId === normalizedCurrentUserId) {
      this.clientId = storedClientId;
      if (!this.userId && storedUserId) {
        this.userId = storedUserId;
      }
      return;
    }

    // UserIds don't match - determine if we should clear or keep clientId

    // Case 1: Logout (filled -> empty) - clear session
    if (normalizedStoredUserId && !normalizedCurrentUserId) {
      this.clearStoredClientInfo();
      return;
    }

    // Case 2: Different logged-in users (filled -> different filled) - clear session for new user
    if (normalizedStoredUserId && normalizedCurrentUserId && normalizedStoredUserId !== normalizedCurrentUserId) {
      this.clearStoredClientInfo();
      return;
    }

    // Case 3: Guest -> Logged-in user (empty -> filled) - keep clientId for merge
    if (!normalizedStoredUserId && normalizedCurrentUserId) {
      this.clientId = storedClientId;
      return;
    }
  }

  private clearStoredClientInfo(): void {
    this.clientId = null;
    this.storedUserId = null;

    if (!CookieManager.canUseCookies()) {
      return;
    }

    this.cookieManager.clearAll();
  }
}
